import { ArrowRight } from "lucide-react";
import { useContent, imgSrc } from "@/context/ContentContext";

const DEFAULT_ITEMS = [
  {
    badge: "Wijnrekken op Maat",
    title: "Houten Wijnrek aan Stenen Muur",
    text: "Vrijhangend en massief houten wijnrekconstructie, afgestemd op de exacte maatvoering van de stenen achterwand en flesformaten.",
    meta: "Materiaal: Eikenhout & Staal",
    tag: "Volledig maatwerk",
    image: "/assets/werk-wijnrek.jpg",
    alt: "Houten wijnrekken gemonteerd aan een karakteristieke stenen muur",
  },
  {
    badge: "Opberg & Erfstukken",
    title: "Ambachtelijke Kist met Deksel",
    text: "Klassieke zwaluwstaartverbindingen, robuust beslag en een zacht sluitend deksel. Gebouwd om tientallen jaren mooi te blijven.",
    meta: "Materiaal: Noten / Eiken",
    tag: "Handgemaakt detail",
    image: "/assets/werk-kist.jpg",
    alt: "Klassieke houten kist met scharnierend deksel en zichtbare houtverbindingen",
  },
  {
    badge: "Kleine Meubels",
    title: "Compacte Meubels & Tafels",
    text: "Bijzettafels, wandplanken, krukjes en audiomeubels die millimeter-nauwkeurig zijn ontworpen voor specifieke woonhoeken.",
    meta: "Materiaal: Hout naar keuze",
    tag: "Uniek design",
    image: "https://images.unsplash.com/photo-1765973916404-21de1613ba22?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwxfHxjdXN0b20lMjB3b29kZW4lMjBjb2ZmZWUlMjB0YWJsZSUyMG9yZ2FuaWMlMjBzaGFwZXxlbnwwfHx8fDE3OTA1MDg0MDN8MA&ixlib=rb-4.1.0&q=85",
    alt: "Compacte maatwerk salontafel met organisch gevormd blad",
  },
  {
    badge: "Horeca & Winkels",
    title: "Winkel- & Horeca-inrichting",
    text: "Karaktervolle balies, displaykasten en serveermeubels die bestand zijn tegen intensief gebruik en sfeer toevoegen.",
    meta: "Doel: Zakelijk maatwerk",
    tag: "Stevig & stijlvol",
    image: "https://images.unsplash.com/photo-1787324570536-e010206cd69a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwxfHx3b29kZW4lMjBiYXIlMjBkaXNwbGF5JTIwY2FiaW5ldCUyMGludGVyaW9yfGVufDB8fHx8MTc5MDUwODQwM3ww&ixlib=rb-4.1.0&q=85",
    alt: "Houten barmeubel en displaykast voor horeca of winkelinterieur",
  },
  {
    badge: "Voor Stylisten",
    title: "Realisatie van Jouw Ontwerp",
    text: "Als interieurontwerper of stylist lever je de schets of 3D-impressie aan; De Houtregisseur vertaalt het naar maakbare perfectie.",
    meta: "Samenwerking: B2B & Pro",
    tag: "Technische regie",
    image: "https://images.unsplash.com/photo-1704697475935-46009ec1d1e7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwxfHxmdXJuaXR1cmUlMjBwcm90b3R5cGUlMjBpbnRlcmlvciUyMGRlc2lnbiUyMGFyY2hpdGVjdHxlbnwwfHx8fDE3OTA1MDg0MDN8MA&ixlib=rb-4.1.0&q=85",
    alt: "Meubelprototype ontwikkeld in samenwerking met een interieurarchitect",
  },
];

export const Gallery = () => {
  const { content } = useContent();
  const items =
    content?.gallery?.length
      ? content.gallery.map((item, i) => ({ ...DEFAULT_ITEMS[i], ...item }))
      : DEFAULT_ITEMS;

  return (
    <section id="collectie" data-testid="gallery-section" className="py-24 bg-[#F2EDE4] px-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6 hr-reveal">
          <div>
            <span className="text-xs uppercase tracking-widest text-[#9C6D3B] font-bold">
              Voorbeeldwerk & Maatwerk
            </span>
            <h2 className="text-3xl sm:text-5xl font-normal font-display text-[#2B231D] mt-2">
              Recent werk uit de werkplaats
            </h2>
          </div>
          <p className="text-sm text-[#6C5E52] max-w-md">
            Ieder stuk is uniek en gemaakt volgens de wensen van de opdrachtgever. Laat
            je inspireren voor jouw eigen idee.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {items.map((item, i) => (
            <div
              key={i}
              data-testid={`gallery-card-${i}`}
              className="group bg-[#FBF9F5] rounded-3xl overflow-hidden border border-[#E2D8CC] shadow-sm hover:shadow-md transition-shadow hr-reveal"
            >
              <div className="aspect-[4/3] relative overflow-hidden">
                <img
                  src={imgSrc(item.image)}
                  alt={item.alt || item.title}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                <span className="absolute bottom-4 left-6 z-10 px-3 py-1 rounded-full bg-black/40 backdrop-blur-md text-white text-xs font-medium border border-white/20">
                  {item.badge}
                </span>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold font-display text-[#2B231D] mb-2">{item.title}</h3>
                <p className="text-sm text-[#6C5E52] leading-relaxed mb-4">{item.text}</p>
                <div className="flex items-center justify-between text-xs text-[#8C7A6B] pt-4 border-t border-[#EAE3DA]">
                  <span>{item.meta}</span>
                  <span className="font-medium text-[#9C6D3B]">{item.tag}</span>
                </div>
              </div>
            </div>
          ))}

          <div
            data-testid="gallery-card-eigen-idee"
            className="bg-[#2B231D] text-white rounded-3xl overflow-hidden border border-[#3E322A] p-8 flex flex-col justify-between hr-reveal"
          >
            <div>
              <span className="text-xs uppercase tracking-widest text-[#E6AF68] font-bold">
                Jouw Project
              </span>
              <h3 className="text-2xl font-bold font-display mt-2 mb-3">
                Heb je zelf een idee of schets?
              </h3>
              <p className="text-sm text-[#D8CFC4] leading-relaxed">
                Geen idee is te gek. Neem contact op en we bekijken samen wat er mogelijk
                is in houtsoort, afmeting en constructie.
              </p>
            </div>
            <div className="mt-8">
              <a
                href="#contact"
                data-testid="gallery-cta-bespreek"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#E6AF68] text-[#1C1612] text-sm font-semibold hover:bg-[#F0C287] transition-colors"
              >
                <span>Bespreek je meubel</span>
                <ArrowRight size={16} />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
